import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AnalysisResponse,
  AnalysisSections,
  AnalysisStatus,
  SectionName,
} from "../../../shared/analysis-types";

const POLL_INTERVAL = 1500;
const POLL_DEADLINE_MS = 10 * 60 * 1000;

type AnalysisStreamEvent =
  | { type: "snapshot"; data: AnalysisResponse }
  | { type: "status"; status: AnalysisStatus; errorMessage?: string | null }
  | { type: "text_delta"; text: string }
  | { type: "thinking_delta"; text: string }
  | { type: "sections"; sections: AnalysisSections; completedSection?: SectionName };

export interface SessionAnalysisState {
  data: AnalysisResponse | null;
  loading: boolean;
  timedOut: boolean;
  restart: () => void;
}

/**
 * Streams `/api/sessions/[id]/analysis/stream` (falling back to polling
 * `/api/sessions/[id]/analysis`) until the session reaches a terminal state
 * or the deadline fires, then stops. Shared by the scorecard slide-out panel
 * and the standalone scorecard page so both surfaces read the exact same
 * polling contract (restart behavior, deadline, terminal states).
 *
 * Transport rides Clerk's same-origin session cookie: EventSource cannot
 * carry an Authorization header, so both the stream and the polling fetch
 * authenticate via cookies.
 */
export function useSessionAnalysis(sessionId: string): SessionAnalysisState {
  const [data, setData] = useState<AnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const [restartToken, setRestartToken] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const startTimeRef = useRef<number>(0);
  const pollFailureCapturedRef = useRef(false);

  const restart = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    setData(null);
    setLoading(true);
    setTimedOut(false);
    pollFailureCapturedRef.current = false;
    startTimeRef.current = Date.now();
    setRestartToken((v) => v + 1);
  }, []);

  useEffect(() => {
    startTimeRef.current = Date.now();
    pollFailureCapturedRef.current = false;

    function capturePollingFailure(error: unknown, status?: number) {
      if (pollFailureCapturedRef.current) return;
      pollFailureCapturedRef.current = true;

      const tags = {
        surface: "analysis",
        source: "client",
        route: "api.sessions.analysis",
        ...(status ? { status: String(status) } : {}),
      };

      if (error instanceof Error) {
        Sentry.captureException(error, { tags });
        return;
      }
      Sentry.captureMessage("Session analysis polling request failed", {
        level: "warning",
        tags,
      });
    }

    function stopPolling() {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    function stopStreaming() {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    }

    function stopIfDeadlineExpired() {
      if (Date.now() - startTimeRef.current > POLL_DEADLINE_MS) {
        setTimedOut(true);
        stopPolling();
        return true;
      }
      return false;
    }

    function isTerminal(responseData: AnalysisResponse) {
      return responseData.status === "complete" || responseData.status === "error";
    }

    async function poll() {
      try {
        const res = await fetch(`/api/sessions/${sessionId}/analysis`, {
          credentials: "same-origin",
        });
        if (!res.ok) {
          capturePollingFailure(null, res.status);
          stopIfDeadlineExpired();
          return;
        }
        const { data: responseData } = (await res.json()) as {
          data: AnalysisResponse;
        };
        setData(responseData);
        setLoading(false);

        if (isTerminal(responseData)) {
          stopPolling();
          return;
        }
        stopIfDeadlineExpired();
      } catch (error) {
        capturePollingFailure(error);
        stopIfDeadlineExpired();
      }
    }

    function startPolling() {
      if (intervalRef.current) return;
      void poll();
      intervalRef.current = setInterval(() => void poll(), POLL_INTERVAL);
    }

    function startStreaming() {
      if (typeof EventSource === "undefined") {
        startPolling();
        return;
      }

      let fallbackStarted = false;
      const source = new EventSource(`/api/sessions/${sessionId}/analysis/stream`, {
        withCredentials: true,
      });
      eventSourceRef.current = source;

      function fallbackToPolling() {
        if (fallbackStarted) return;
        fallbackStarted = true;
        stopStreaming();
        startPolling();
      }

      source.addEventListener("analysis", (event) => {
        try {
          const streamEvent = JSON.parse(event.data) as AnalysisStreamEvent;
          const terminal = isTerminalStreamEvent(streamEvent);
          setData((current) => applyAnalysisStreamEvent(current, streamEvent));
          setLoading(false);
          if (terminal) {
            stopStreaming();
            stopPolling();
          }
        } catch (error) {
          capturePollingFailure(error);
          fallbackToPolling();
        }
      });

      source.addEventListener("timeout", () => {
        fallbackToPolling();
      });

      source.onerror = () => {
        if (eventSourceRef.current === source) {
          fallbackToPolling();
        }
      };
    }

    startStreaming();
    return () => {
      stopStreaming();
      stopPolling();
    };
  }, [sessionId, restartToken]);

  return { data, loading, timedOut, restart };
}

function isTerminalStreamEvent(event: AnalysisStreamEvent): boolean {
  if (event.type === "snapshot") {
    return event.data.status === "complete" || event.data.status === "error";
  }
  if (event.type === "status") {
    return event.status === "complete" || event.status === "error";
  }
  return false;
}

function applyAnalysisStreamEvent(
  current: AnalysisResponse | null,
  event: AnalysisStreamEvent,
): AnalysisResponse {
  const base: AnalysisResponse = current ?? {
    status: "analyzing",
    sections: {},
    thinking: "",
    hasStreamingText: false,
  };

  switch (event.type) {
    case "snapshot":
      return event.data;
    case "status":
      return {
        ...base,
        status: event.status,
        ...(event.errorMessage ? { errorMessage: event.errorMessage } : {}),
      };
    case "text_delta":
      return {
        ...base,
        status: base.status === "pending" ? "analyzing" : base.status,
        hasStreamingText: true,
      };
    case "thinking_delta":
      return {
        ...base,
        status: base.status === "pending" ? "analyzing" : base.status,
        thinking: `${base.thinking ?? ""}${event.text}`,
      };
    case "sections":
      return {
        ...base,
        status: base.status === "pending" ? "analyzing" : base.status,
        sections: { ...base.sections, ...event.sections },
        hasStreamingText: true,
      };
  }
}
